/**
 * Exit-intent + cart-idle incentive popup (WELCOME15 / same welcome code).
 *
 * Reliability notes:
 * - Once per browser session (sessionStorage), not multi-day localStorage.
 * - Exit intent uses mouseleave on <html> (more reliable than mouseout).
 * - Timed fallback so the offer still appears if exit intent never fires.
 * - Cart idle ignores scroll (scroll was resetting the timer constantly).
 */
const STORAGE_SUBSCRIBED = 'cj_incentive_popup_subscribed';
const STORAGE_SESSION_SHOWN = 'cj_incentive_popup_session_shown';

class IncentivePopup extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #cartIdleTimer = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #fallbackTimer = null;
  /** @type {number} */
  #cartCount = 0;
  /** @type {boolean} */
  #shownThisPage = false;
  /** @type {boolean} */
  #armed = false;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#cartCount = Number(this.dataset.cartCount || 0);

    if (this.#forceShowRequested()) {
      this.#bindClose();
      this.#show(this.#cartCount > 0 ? 'cart' : 'exit');
      return;
    }

    if (this.dataset.justSubscribed === 'true') {
      this.#markSubscribed();
      this.#bindClose();
      this.#show('success');
      return;
    }

    if (this.dataset.formError === 'true') {
      this.#bindClose();
      this.#show('exit');
      return;
    }

    if (this.#isSuppressed()) return;

    this.#bindClose();
    this.#bindExitIntent();
    this.#bindCartEvents();
    this.#bindActivity();
    this.#armAndSchedule();

    if (this.#cartCount > 0) this.#restartCartIdle();
  }

  disconnectedCallback() {
    this.#clearCartIdle();
    this.#clearFallback();
    document.documentElement.removeEventListener('mouseleave', this.#onHtmlMouseLeave);
    document.removeEventListener('mouseout', this.#onDocumentMouseOut);
    document.removeEventListener('cart:update', this.#onCartUpdate);
    ['pointerdown', 'keydown', 'touchstart'].forEach((eventName) => {
      window.removeEventListener(eventName, this.#onActivity, { capture: true });
    });
  }

  #forceShowRequested() {
    try {
      return new URLSearchParams(window.location.search).get('show_incentive') === '1';
    } catch {
      return false;
    }
  }

  #isSuppressed() {
    if (this.dataset.designMode === 'true') return false;

    try {
      if (localStorage.getItem(STORAGE_SUBSCRIBED) === '1') {
        // Subscribed visitors only see cart reminder when they have items.
        return this.#cartCount <= 0;
      }
      if (sessionStorage.getItem(STORAGE_SESSION_SHOWN) === '1') return true;
    } catch {
      // Ignore storage errors (private mode, etc.)
    }
    return false;
  }

  #isSubscribed() {
    try {
      return localStorage.getItem(STORAGE_SUBSCRIBED) === '1';
    } catch {
      return false;
    }
  }

  #markSubscribed() {
    try {
      localStorage.setItem(STORAGE_SUBSCRIBED, '1');
    } catch {
      // ignore
    }
  }

  #markSessionShown() {
    try {
      sessionStorage.setItem(STORAGE_SESSION_SHOWN, '1');
    } catch {
      // ignore
    }
  }

  #armAndSchedule() {
    const minSeconds = Math.max(1, Number(this.dataset.minSeconds || 5));
    const fallbackSeconds = Math.max(
      minSeconds + 1,
      Number(this.dataset.fallbackSeconds || 20)
    );

    window.setTimeout(() => {
      this.#armed = true;
    }, minSeconds * 1000);

    // Backup when exit intent never fires (common on trackpads / mobile).
    this.#fallbackTimer = window.setTimeout(() => {
      if (this.#shownThisPage || this.#isSuppressed()) return;
      this.#trigger(this.#cartCount > 0 ? 'cart' : 'exit');
    }, fallbackSeconds * 1000);
  }

  #clearFallback() {
    if (this.#fallbackTimer) {
      clearTimeout(this.#fallbackTimer);
      this.#fallbackTimer = null;
    }
  }

  #bindClose() {
    this.querySelectorAll('[data-incentive-close]').forEach((button) => {
      button.addEventListener('click', () => this.#close());
    });

    this.#dialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#close();
    });

    this.#dialog?.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#close();
    });
  }

  #bindExitIntent() {
    // mouseleave on <html> is the most reliable desktop exit signal.
    document.documentElement.addEventListener('mouseleave', this.#onHtmlMouseLeave);
    // Fallback for browsers that only bubble mouseout when leaving the viewport.
    document.addEventListener('mouseout', this.#onDocumentMouseOut);
  }

  #onHtmlMouseLeave = (event) => {
    if (!this.#armed || this.#shownThisPage) return;
    // Top chrome / tab area — classic exit intent.
    if (typeof event.clientY === 'number' && event.clientY > 8) return;
    this.#trigger(this.#cartCount > 0 ? 'cart' : 'exit');
  };

  #onDocumentMouseOut = (event) => {
    if (!this.#armed || this.#shownThisPage) return;
    // Only when pointer leaves the document (no element receiving it).
    const to = /** @type {MouseEvent} */ (event).relatedTarget;
    if (to) return;
    if (typeof event.clientY === 'number' && event.clientY > 8) return;
    this.#trigger(this.#cartCount > 0 ? 'cart' : 'exit');
  };

  #bindCartEvents() {
    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    const count =
      detail?.data?.itemCount ?? detail?.resource?.item_count ?? detail?.data?.item_count;

    if (typeof count === 'number') {
      this.#cartCount = count;
      if (this.#cartCount > 0) this.#restartCartIdle();
      else this.#clearCartIdle();
      return;
    }

    this.#refreshCartCount();
  };

  async #refreshCartCount() {
    // Avoid /cart.js polling — Shopify bot protection often flags Ajax cart traffic,
    // especially on theme preview (127.0.0.1), and can break the next form POST.
    const bubble = document.querySelector('cart-icon, .cart-bubble, [data-cart-count]');
    const attrCount = bubble?.getAttribute?.('data-cart-count');
    if (attrCount != null && attrCount !== '') {
      this.#cartCount = Number(attrCount) || 0;
    }
    if (this.#cartCount > 0) this.#restartCartIdle();
    else this.#clearCartIdle();
  }

  #bindActivity() {
    // Intentionally no scroll listener — scrolling was resetting cart idle forever.
    ['pointerdown', 'keydown', 'touchstart'].forEach((eventName) => {
      window.addEventListener(eventName, this.#onActivity, { capture: true, passive: true });
    });
  }

  #onActivity = () => {
    if (this.#cartCount > 0 && !this.#shownThisPage) this.#restartCartIdle();
  };

  #restartCartIdle() {
    this.#clearCartIdle();
    if (this.#cartCount <= 0 || this.#shownThisPage || this.#isSuppressed()) return;

    const idleMs = Number(this.dataset.cartIdleSeconds || 30) * 1000;
    this.#cartIdleTimer = window.setTimeout(() => {
      if (!this.#armed) {
        this.#restartCartIdle();
        return;
      }
      this.#trigger('cart');
    }, idleMs);
  }

  #clearCartIdle() {
    if (this.#cartIdleTimer) {
      clearTimeout(this.#cartIdleTimer);
      this.#cartIdleTimer = null;
    }
  }

  /**
   * @param {'exit' | 'cart' | 'success' | 'remind'} mode
   */
  #trigger(mode) {
    if (this.#shownThisPage) return;
    if (this.#dialog?.open) return;
    if (this.#isSuppressed() && mode !== 'success') return;

    if (this.#isSubscribed()) {
      if (this.#cartCount > 0) this.#show('remind');
      return;
    }

    this.#show(mode === 'cart' ? 'cart' : 'exit');
  }

  /**
   * @param {'exit' | 'cart' | 'success' | 'remind'} mode
   */
  #show(mode) {
    if (!this.#dialog) return;

    const capture = this.querySelector('[data-panel="capture"]');
    const success = this.querySelector('[data-panel="success"]');
    const remind = this.querySelector('[data-panel="remind"]');

    if (mode === 'success') {
      capture?.setAttribute('hidden', '');
      remind?.setAttribute('hidden', '');
      success?.removeAttribute('hidden');
    } else if (mode === 'remind') {
      capture?.setAttribute('hidden', '');
      success?.setAttribute('hidden', '');
      remind?.removeAttribute('hidden');
    } else {
      success?.setAttribute('hidden', '');
      remind?.setAttribute('hidden', '');
      capture?.removeAttribute('hidden');
      this.#applyCopy(mode);
    }

    this.#shownThisPage = true;
    this.#clearCartIdle();
    this.#clearFallback();
    this.#markSessionShown();
    this.#dialog.classList.remove('incentive-popup__dialog--closing');

    try {
      this.#dialog.showModal();
    } catch {
      // showModal can throw if already open or dialog not in document.
      this.#dialog.setAttribute('open', '');
    }

    const emailInput = this.querySelector('#IncentivePopup-Email');
    if (emailInput instanceof HTMLInputElement && mode !== 'success' && mode !== 'remind') {
      window.setTimeout(() => emailInput.focus(), 50);
    }
  }

  /**
   * @param {'exit' | 'cart'} mode
   */
  #applyCopy(mode) {
    const heading = this.querySelector('#incentive-popup-heading');
    const text = this.querySelector('[data-panel="capture"] .incentive-popup__text');
    if (!(heading instanceof HTMLElement) || !(text instanceof HTMLElement)) return;

    if (mode === 'cart') {
      heading.textContent = heading.dataset.headingCart || heading.textContent;
      text.textContent = text.dataset.textCart || text.textContent;
    } else {
      heading.textContent = heading.dataset.headingExit || heading.textContent;
      text.textContent = text.dataset.textExit || text.textContent;
    }
  }

  async #close() {
    if (!this.#dialog?.open) return;

    this.#dialog.classList.add('incentive-popup__dialog--closing');
    await new Promise((resolve) => {
      const done = () => {
        this.#dialog?.removeEventListener('animationend', done);
        resolve(undefined);
      };
      this.#dialog?.addEventListener('animationend', done, { once: true });
      window.setTimeout(done, 320);
    });

    this.#dialog.classList.remove('incentive-popup__dialog--closing');
    this.#dialog.close();
  }
}

if (!customElements.get('incentive-popup')) {
  customElements.define('incentive-popup', IncentivePopup);
}
